import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Calendar, User, Building, MapPin, DollarSign, FileText, Download, Mail, Phone, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const ManagerArchivedOfferDetailPage = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [offer, setOffer] = useState(null);
  const [candidatures, setCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOfferDetails();
    fetchCandidatures();
  }, [offerId]);

  const fetchOfferDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/${offerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const offerData = await response.json();
        setOffer(offerData);
      } else {
        toast.error('Erreur lors du chargement de l\'offre');
        navigate('/manager/offres-archivees');
      }
    } catch (error) {
      console.error('Error fetching offer details:', error);
      toast.error('Erreur de connexion');
      navigate('/manager/offres-archivees');
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidatures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/candidatures/job/${offerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const candidaturesData = await response.json();
        setCandidatures(candidaturesData);
      }
    } catch (error) {
      console.error('Error fetching candidatures:', error);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBackToArchived = () => {
    navigate('/manager/offres-archivees');
  };

  // Fonction pour vérifier si l'offre peut être restaurée (moins de 24h depuis archivage)
  const canRestoreOffer = (archiveDate) => {
    if (!archiveDate) return false;
    
    const archived = new Date(archiveDate);
    const now = new Date();
    const timeDifference = now.getTime() - archived.getTime();
    const hoursDifference = timeDifference / (1000 * 3600); // Convertir en heures
    
    return hoursDifference < 24;
  };

  // Fonction pour restaurer l'offre
  const handleRestoreOffer = async () => {
    if (!offer) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/${offer.id}/status?status=en_cours`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Offre restaurée avec succès dans "En cours"');
        navigate('/manager');
      } else {
        toast.error('Erreur lors de la restauration de l\'offre');
      }
    } catch (error) {
      console.error('Error restoring offer:', error);
      toast.error('Erreur de connexion');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'accepte':
        return 'candidature-status-compact status-accepte';
      case 'refuse':
        return 'candidature-status-compact status-refuse';
      default:
        return 'candidature-status-compact status-nouveau';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'accepte':
        return 'Accepté';
      case 'refuse':
        return 'Refusé';
      default:
        return 'Nouveau';
    }
  };

  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Horizon Solution': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-destructive mb-4">Offre non trouvée</h1>
          <Button onClick={handleBackToArchived}>Retour aux offres archivées</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="archived-offer-detail-page">
      {/* Header */}
      <header className="rh-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBackToArchived}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos[offer.company]}
            alt={`Logo ${offer.company}`}
            className="rh-header-logo"
          />
        </div>
        <h1>Détail de l'offre archivée - {user?.company}</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {/* Archive Notice */}
        <div className="archive-notice">
          <div className="archive-notice-content">
            <span className="archive-badge">ARCHIVÉE</span>
            <span className="archive-text">
              Cette offre est archivée et en lecture seule
              {offer.updated_at && (
                <span className="archive-date">
                  - Archivée le {formatDate(offer.updated_at)}
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Offer Header */}
        <div className="offer-detail-header-compact">
          <h2>{offer.company} : {offer.title}</h2>
          <div className="offer-detail-info-line">
            <div className="offer-detail-info-item-compact">
              <User className="offer-detail-info-icon-compact" />
              <span>{offer.creator_name || 'Inconnu'}</span>
            </div>
            <div className="offer-detail-info-separator">•</div>
            <div className="offer-detail-info-item-compact">
              <Calendar className="offer-detail-info-icon-compact" />
              <span>{formatDate(offer.date_arrivee)}</span>
            </div>
            <div className="offer-detail-info-separator">•</div>
            <div className="offer-detail-info-item-compact">
              <MapPin className="offer-detail-info-icon-compact" />
              <span>{offer.lieu_travail || 'Non spécifié'}</span>
            </div>
            {offer.type_poste && (
              <>
                <div className="offer-detail-info-separator">•</div>
                <div className="offer-detail-info-item-compact">
                  <span>{offer.type_poste}</span>
                </div>
              </>
            )}
            {offer.taux_horaire && (
              <>
                <div className="offer-detail-info-separator">•</div>
                <div className="offer-detail-info-item-compact">
                  <DollarSign className="offer-detail-info-icon-compact" />
                  <span>{offer.taux_horaire}€/h</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Offer Description */}
        <Card className="offer-description-card-compact">
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold mb-3">Description détaillée</h3>
            <div className="offer-description-content-compact">
              <div className="mb-3">
                <h4 className="font-medium mb-1 text-sm text-primary">Missions</h4>
                <p className="text-gray-700 text-sm whitespace-pre-wrap">{offer.description}</p>
              </div>
              {offer.qualites_attendues && (
                <div>
                  <h4 className="font-medium mb-1 text-sm text-primary">Qualités attendues</h4>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{offer.qualites_attendues}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Candidatures Table */}
        <Card className="candidatures-card-compact">
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">
                Candidatures ({candidatures.length})
              </h3>
              {/* Bouton Restaurer - affiché seulement si moins de 24h depuis archivage */}
              {offer.updated_at && canRestoreOffer(offer.updated_at) && (
                <Button
                  onClick={handleRestoreOffer}
                  className="btn-restore"
                  style={{ backgroundColor: '#22c55e', borderColor: '#22c55e', color: 'white' }}
                >
                  <RotateCcw size={16} className="mr-2" />
                  Restaurer
                </Button>
              )}
            </div>
            <div className="candidatures-table-container-compact">
              <table className="candidatures-table-compact">
                <thead>
                  <tr>
                    <th>Candidat</th>
                    <th>Documents</th>
                    <th>Contact</th>
                    <th>Commentaire</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {candidatures.map((candidature) => (
                    <tr key={candidature.id} className={candidature.status === 'refuse' ? 'candidature-refused' : ''}>
                      <td>
                        <div className="candidature-name-compact">
                          {candidature.prenom} {candidature.nom}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-files-compact">
                          <span className="text-gray-400 text-xs">Lecture seule</span>
                        </div>
                      </td>
                      <td>
                        <div className="candidature-contact-compact">
                          {(candidature.manager_email || candidature.email) && (
                            <div className="contact-item-compact">
                              <Mail size={12} />
                              <span>{candidature.manager_email || candidature.email}</span>
                            </div>
                          )}
                          {(candidature.manager_telephone || candidature.telephone) && (
                            <div className="contact-item-compact">
                              <Phone size={12} />
                              <span>{candidature.manager_telephone || candidature.telephone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-comment-compact">
                          {candidature.commentaire || '-'}
                          {candidature.raison_refus && (
                            <div className="refus-reason">
                              <strong>Refus:</strong> {candidature.raison_refus}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={getStatusStyle(candidature.status)}>
                          {getStatusText(candidature.status)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {candidatures.length === 0 && (
              <div className="empty-state-compact">
                <p>Aucune candidature pour cette offre</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ManagerArchivedOfferDetailPage;